import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { authService } from '../api/authService';
import { userService } from '../api/userService';
import { STORAGE_KEYS, clearAuthTokens, extractErrorMessage, getAccessToken, getRefreshToken, setAuthTokens } from '../api/client';
import SuspendedModal from '../components/SuspendedModal';
import { WelcomeOfferProvider } from '../offers/WelcomeOfferContext';
import WelcomeOfferBanner from '../offers/WelcomeOfferBanner';

const AuthContext = createContext(null);

const parseJwt = (token) => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    return null;
  }
};

const getStoredUser = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser());
  const [token, setToken] = useState(getAccessToken());
  const [isLoading, setIsLoading] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);
  const refreshTimeoutRef = useRef(null);

  const setUserState = useCallback((nextUser) => {
    setUser(nextUser);
    if (nextUser) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(nextUser));
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER);
    }
  }, []);

  const scheduleRefresh = useCallback((accessToken) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    if (!accessToken) return;

    const payload = parseJwt(accessToken);
    const expiresAt = payload?.exp ? payload.exp * 1000 : null;
    if (!expiresAt) return;

    const refreshAt = Math.max(expiresAt - Date.now() - 60000, 10000);
    refreshTimeoutRef.current = setTimeout(async () => {
      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) return;
        const result = await authService.refreshToken(refreshToken);
        if (result.access_token) {
          setAuthTokens(result.access_token, result.refresh_token);
          setToken(result.access_token);
          scheduleRefresh(result.access_token);
        }
      } catch (error) {
        clearAuthTokens();
        setToken(null);
        setUserState(null);
      }
    }, refreshAt);
  }, [setUserState]);

  const hydrateUser = useCallback(async () => {
    const accessToken = getAccessToken();
    const storedUser = getStoredUser();
    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    setToken(accessToken);
    scheduleRefresh(accessToken);

    // Show stored user immediately for fast UX, but always verify with backend
    if (storedUser) {
      setUser(storedUser);
    }

    try {
      const profile = await userService.getMe();
      if (profile) {
        setUserState(profile);
      }
    } catch (error) {
      // The axios interceptor handles 403 suspended globally via the event,
      // so if we get here with a suspension error, isBlocked is already set.
      // For other errors, clear auth.
      const errorMessage = extractErrorMessage(error);
      if (errorMessage.includes('suspended') || errorMessage.includes('unusual activity')) {
        setIsBlocked(true);
      } else {
        clearAuthTokens();
        setToken(null);
        setUserState(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [scheduleRefresh, setUserState]);

  useEffect(() => {
    hydrateUser();

    // Listen for global account-suspended events from the axios interceptor
    const handleSuspended = () => setIsBlocked(true);
    window.addEventListener('account-suspended', handleSuspended);

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      window.removeEventListener('account-suspended', handleSuspended);
    };
  }, [hydrateUser]);

  const login = useCallback(async (email, password) => {
    try {
      const result = await authService.login(email, password);
      if (result.access_token) {
        setAuthTokens(result.access_token, result.refresh_token);
        setToken(result.access_token);
        scheduleRefresh(result.access_token);
      }
      if (result.user) {
        setUserState(result.user);
        // Check if user is blocked
        if (result.user.is_blocked) {
          setIsBlocked(true);
        }
      } else {
        const profile = await userService.getMe();
        if (profile) {
          setUserState(profile);
          // Check if user is blocked
          if (profile.is_blocked) {
            setIsBlocked(true);
          }
        }
      }
      return result;
    } catch (error) {
      // Check if error is due to account suspension
      const message = extractErrorMessage(error);
      if (message.includes('suspended') || message.includes('unusual activity')) {
        setIsBlocked(true);
      }
      throw new Error(message);
    }
  }, [scheduleRefresh, setUserState]);

  const signup = useCallback(async (email, password, firstName, lastName, phoneNumber = null) => {
    try {
      const result = await authService.signup({ email, password, firstName, lastName, phoneNumber });
      if (result.access_token) {
        setAuthTokens(result.access_token, result.refresh_token);
        setToken(result.access_token);
        scheduleRefresh(result.access_token);
      }
      if (result.user) {
        setUserState(result.user);
      } else {
        const profile = await userService.getMe();
        if (profile) setUserState(profile);
      }
      return result;
    } catch (error) {
      // Re-throw with user-friendly message
      const message = extractErrorMessage(error);
      throw new Error(message);
    }
  }, [scheduleRefresh, setUserState]);

  const loginWithGoogle = useCallback(async (idToken) => {
    try {
      const result = await authService.googleAuth(idToken);
      if (result.access_token) {
        setAuthTokens(result.access_token, result.refresh_token);
        setToken(result.access_token);
        scheduleRefresh(result.access_token);
      }
      if (result.user) {
        setUserState(result.user);
      } else {
        const profile = await userService.getMe();
        if (profile) setUserState(profile);
      }
      return result;
    } catch (error) {
      // Re-throw with user-friendly message
      const message = extractErrorMessage(error);
      throw new Error(message);
    }
  }, [scheduleRefresh, setUserState]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Ignore logout failures
    }
    clearAuthTokens();
    setToken(null);
    setUserState(null);
  }, [setUserState]);

  const refreshToken = useCallback(async () => {
    const refreshTokenValue = getRefreshToken();
    if (!refreshTokenValue) return null;
    const result = await authService.refreshToken(refreshTokenValue);
    if (result.access_token) {
      setAuthTokens(result.access_token, result.refresh_token);
      setToken(result.access_token);
      scheduleRefresh(result.access_token);
    }
    return result;
  }, [scheduleRefresh]);

  const value = {
    user,
    token,
    isAuthenticated: !!token,
    isLoading,
    isBlocked,
    login,
    signup,
    loginWithGoogle,
    logout,
    refreshToken,
    setUserState
  };

  return (
    <AuthContext.Provider value={value}>
      <WelcomeOfferProvider>
        {children}
        {isBlocked && <SuspendedModal />}
        <WelcomeOfferBanner />
      </WelcomeOfferProvider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
